import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import { PageContent } from '../components/Layout'
import { useAuth } from '../hooks/useAuth'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'

interface LeadStatus {
  id: number
  name: string
  color: string
  position: number
}

interface UrgencyLevel {
  id: number
  name: string
  color: string
  position: number
}

const LeadsSettings: React.FC = () => {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const { user } = useAuth()

  // State for lead statuses and urgency levels
  const [leadStatuses, setLeadStatuses] = useState<LeadStatus[]>([])
  const [urgencyLevels, setUrgencyLevels] = useState<UrgencyLevel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Modal states
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean
    editing: boolean
    editingId?: number
  }>({ isOpen: false, editing: false })

  const [urgencyModal, setUrgencyModal] = useState<{
    isOpen: boolean
    editing: boolean
    editingId?: number
  }>({ isOpen: false, editing: false })

  // Form states
  const [statusForm, setStatusForm] = useState({
    name: '',
    color: '#3B82F6'
  })

  const [urgencyForm, setUrgencyForm] = useState({
    name: '',
    color: '#EF4444'
  })

  // Message state
  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [statusesResponse, urgencyResponse] = await Promise.all([
          makeApiCall('/api/lead-settings/statuses'),
          makeApiCall('/api/lead-settings/urgency-levels')
        ])

        if (statusesResponse.ok) {
          const statuses = await statusesResponse.json()
          setLeadStatuses(statuses)
        }

        if (urgencyResponse.ok) {
          const urgency = await urgencyResponse.json()
          setUrgencyLevels(urgency)
        }
      } catch (error) {
        console.error('Error loading data:', error)
        showMessage('error', t('leadSettings.failedToLoadSettings'))
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  // Status management functions
  const openStatusModal = (status?: LeadStatus) => {
    if (status) {
      setStatusForm({
        name: status.name,
        color: status.color
      })
      setStatusModal({ isOpen: true, editing: true, editingId: status.id })
    } else {
      setStatusForm({ name: '', color: '#3B82F6' })
      setStatusModal({ isOpen: true, editing: false })
    }
  }

  const closeStatusModal = () => {
    setStatusModal({ isOpen: false, editing: false })
    setStatusForm({ name: '', color: '#3B82F6' })
  }

  const handleSaveStatus = async () => {
    if (!statusForm.name.trim()) return
    
    try {
      setSaving(true)
      const endpoint = statusModal.editing 
        ? `/api/lead-settings/statuses/${statusModal.editingId}`
        : '/api/lead-settings/statuses'
      const method = statusModal.editing ? 'PUT' : 'POST'
      
      await makeJsonApiCall(endpoint, method, statusForm)
      
      // Reload statuses
      const response = await makeApiCall('/api/lead-settings/statuses')
      if (response.ok) {
        const statuses = await response.json()
        setLeadStatuses(statuses)
      }
      
      closeStatusModal()
      showMessage('success', statusModal.editing ? t('leadSettings.statusUpdatedSuccess') : t('leadSettings.statusCreatedSuccess'))
    } catch (error) {
      showMessage('error', t('leadSettings.failedToSaveStatus'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteStatus = async (id: number) => {
    if (!confirm(t('leadSettings.confirmDeleteStatus'))) return
    
    try {
      await makeApiCall(`/api/lead-settings/statuses/${id}`, { method: 'DELETE' })
      
      // Reload statuses
      const response = await makeApiCall('/api/lead-settings/statuses')
      if (response.ok) {
        const statuses = await response.json()
        setLeadStatuses(statuses)
      }
      
      showMessage('success', t('leadSettings.statusDeletedSuccess'))
    } catch (error) {
      showMessage('error', t('leadSettings.failedToDeleteStatus'))
    }
  }

  // Urgency management functions
  const openUrgencyModal = (urgency?: UrgencyLevel) => {
    if (urgency) {
      setUrgencyForm({
        name: urgency.name,
        color: urgency.color
      })
      setUrgencyModal({ isOpen: true, editing: true, editingId: urgency.id })
    } else {
      setUrgencyForm({ name: '', color: '#EF4444' })
      setUrgencyModal({ isOpen: true, editing: false })
    }
  }

  const closeUrgencyModal = () => {
    setUrgencyModal({ isOpen: false, editing: false })
    setUrgencyForm({ name: '', color: '#EF4444' })
  }

  const handleSaveUrgency = async () => {
    if (!urgencyForm.name.trim()) return
    
    try {
      setSaving(true)
      const endpoint = urgencyModal.editing 
        ? `/api/lead-settings/urgency-levels/${urgencyModal.editingId}`
        : '/api/lead-settings/urgency-levels'
      const method = urgencyModal.editing ? 'PUT' : 'POST'
      
      await makeJsonApiCall(endpoint, method, urgencyForm)
      
      // Reload urgency levels
      const response = await makeApiCall('/api/lead-settings/urgency-levels')
      if (response.ok) {
        const urgency = await response.json()
        setUrgencyLevels(urgency)
      }
      
      closeUrgencyModal()
      showMessage('success', urgencyModal.editing ? t('leadSettings.urgencyUpdatedSuccess') : t('leadSettings.urgencyCreatedSuccess'))
    } catch (error) {
      showMessage('error', t('leadSettings.failedToSaveUrgency'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteUrgency = async (id: number) => {
    if (!confirm(t('leadSettings.confirmDeleteUrgency'))) return
    
    try {
      await makeApiCall(`/api/lead-settings/urgency-levels/${id}`, { method: 'DELETE' })
      
      // Reload urgency levels
      const response = await makeApiCall('/api/lead-settings/urgency-levels')
      if (response.ok) {
        const urgency = await response.json()
        setUrgencyLevels(urgency)
      }
      
      showMessage('success', t('leadSettings.urgencyDeletedSuccess'))
    } catch (error) {
      showMessage('error', t('leadSettings.failedToDeleteUrgency'))
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('leadSettings.title')}
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('leadSettings.subtitle')}
          </p>
        </div>

        {/* Success/Error Messages */}
        {message && (
          <div className={`p-4 rounded-md ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Lead Statuses Section */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow mb-8`}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('leadSettings.leadStatuses')}
              </h2>
              <button
                onClick={() => openStatusModal()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800"
              >
                {t('leadSettings.addStatus')}
              </button>
            </div>
          </div>
          <div className="p-6">
            {leadStatuses.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('leadSettings.noStatusesConfigured')}
              </p>
            ) : (
              <div className="space-y-3">
                {leadStatuses.map((status) => (
                  <div key={status.id} className={`flex items-center justify-between p-4 rounded-lg border ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {status.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openStatusModal(status)}
                        className={`text-sm px-3 py-1 rounded ${isDark ? 'text-gray-600 hover:bg-gray-600' : 'text-gray-900 hover:bg-blue-50'}`}
                      >
                        {t('leadSettings.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteStatus(status.id)}
                        className={`text-sm px-3 py-1 rounded ${isDark ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}
                      >
                        {t('leadSettings.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Urgency Levels Section */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow mb-8`}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('leadSettings.urgencyLevels')}
              </h2>
              <button
                onClick={() => openUrgencyModal()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                {t('leadSettings.addUrgencyLevel')}
              </button>
            </div>
          </div>
          <div className="p-6">
            {urgencyLevels.length === 0 ? (
              <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('leadSettings.noUrgencyLevelsConfigured')}
              </p>
            ) : (
              <div className="space-y-3">
                {urgencyLevels.map((urgency) => (
                  <div key={urgency.id} className={`flex items-center justify-between p-4 rounded-lg border ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: urgency.color }}
                      />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {urgency.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openUrgencyModal(urgency)}
                        className={`text-sm px-3 py-1 rounded ${isDark ? 'text-gray-600 hover:bg-gray-600' : 'text-gray-900 hover:bg-blue-50'}`}
                      >
                        {t('leadSettings.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteUrgency(urgency.id)}
                        className={`text-sm px-3 py-1 rounded ${isDark ? 'text-red-400 hover:bg-gray-600' : 'text-red-600 hover:bg-red-50'}`}
                      >
                        {t('leadSettings.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Modal */}
      {statusModal.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {statusModal.editing ? t('leadSettings.editStatus') : t('leadSettings.addNewStatus')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('leadSettings.name')}</label>
                  <input
                    type="text"
                    value={statusForm.name}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={t('leadSettings.statusName')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('leadSettings.color')}</label>
                  <input
                    type="color"
                    value={statusForm.color}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, color: e.target.value }))}
                    className="mt-1 w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeStatusModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  {t('leadSettings.cancel')}
                </button>
                <button
                  onClick={handleSaveStatus}
                  disabled={saving || !statusForm.name.trim()}
                  className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {saving ? t('leadSettings.saving') : (statusModal.editing ? t('leadSettings.update') : t('leadSettings.create'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Urgency Modal */}
      {urgencyModal.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {urgencyModal.editing ? t('leadSettings.editUrgencyLevel') : t('leadSettings.addNewUrgencyLevel')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('leadSettings.name')}</label>
                  <input
                    type="text"
                    value={urgencyForm.name}
                    onChange={(e) => setUrgencyForm(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={t('leadSettings.urgencyLevelName')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('leadSettings.color')}</label>
                  <input
                    type="color"
                    value={urgencyForm.color}
                    onChange={(e) => setUrgencyForm(prev => ({ ...prev, color: e.target.value }))}
                    className="mt-1 w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeUrgencyModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  {t('leadSettings.cancel')}
                </button>
                <button
                  onClick={handleSaveUrgency}
                  disabled={saving || !urgencyForm.name.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? t('leadSettings.saving') : (urgencyModal.editing ? t('leadSettings.update') : t('leadSettings.create'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContent>
  )
}

export default LeadsSettings